import { ErrorModal, useUserContext } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { SectionFamily } from "../models/SectionFamily";
import { getSectionFamilies } from "../repositories/SectionFamilyRepository";

interface Props {
	label: string;
	assetId?: number;
	value: number | null;
	fieldError?: FieldError;
	onChange?: Function;
	disabled?: boolean;
	required?: boolean;
}

const useSectionFamilies = (
	sectionFamilyId?: number,
	onError?: (error: Error) => void,
	assetId?: number,
) => {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const effectiveAssetId = assetId ?? defaultAreaId;
	return useQuery<SectionFamily[], Error>({
		queryKey: ["sectionFamilies", sectionFamilyId, effectiveAssetId],
		queryFn: ({ signal }) =>
			getSectionFamilies(
				{ sectionFamilyId, defaultAreaId: effectiveAssetId },
				signal,
			),
		onError: onError,
	});
};

export const SectionFamilySelector = ({
	label,
	assetId,
	value,
	fieldError,
	onChange = () => {},
	disabled = false,
	required = false,
}: Props) => {
	const [error, setError] = useState("");

	const { data: sectionFamilies, isLoading } = useSectionFamilies(
		undefined,
		(error) => setError(error.message),
		assetId,
	);

	const selectedValue =
		sectionFamilies?.find((section) => section.SectionFamilyId === value) ||
		null;

	return (
		<>
			<Autocomplete
				clearOnEscape
				autoHighlight
				options={sectionFamilies || []}
				getOptionLabel={(option) => option.SectionFamilyName}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						margin="none"
						error={!!fieldError}
						helperText={fieldError?.message}
						inputProps={{
							...params.inputProps,
							"data-test-id": "section-family-selector",
						}}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										params.InputProps.endAdornment
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedValue}
				onChange={(event, newValue) => {
					onChange(newValue?.SectionFamilyId);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading section families"
			/>
		</>
	);
};
